'use client';

import { useButton } from '@react-aria/button';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from 'react-aria';
import { useRef } from 'react';
import { Button, type ButtonProps } from '../Button';

export type AriaButtonProps = Parameters<typeof useButton>[0] &
  Pick<
    ButtonProps,
    'variant' | 'size' | 'layout' | 'shape' | 'className' | 'children'
  >;

/**
 * Bridges React Aria button behavior (e.g. calendar prev/next `onPress`) with v5 {@link Button} styles.
 *
 * Internal primitive for v5 design-system components only — do not use as a standalone button in app code.
 * Prefer {@link Button} for general UI; use this only when composing another v5 component that needs React Aria's button API.
 */
export function AriaButton({
  children,
  className,
  variant = 'ghost',
  size = 'sm',
  layout = 'icon',
  shape,
  isDisabled,
  ...props
}: AriaButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const { buttonProps, isPressed } = useButton({ ...props, isDisabled }, ref);
  const { focusProps } = useFocusRing();
  const disabled = Boolean(isDisabled);

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      layout={layout}
      shape={shape}
      disabled={disabled}
      className={className}
      aria-pressed={isPressed}
      {...mergeProps(buttonProps, focusProps)}
    >
      {children}
    </Button>
  );
}

AriaButton.displayName = 'AriaButton';
